/**
 * SSE subscription for live KI-Hofassistent "thinking" step events (#1776).
 *
 * The backend publishes one ``chat:assistant.step`` per ReAct tool call
 * (fire-and-forget, never persisted); the BFF relays it on the shared
 * ``/api/events`` stream. This hook is a DUMB subscription — it parses the
 * payload and forwards it to ``onStep``. All display gating (pending turn,
 * thread matching, reset on resolve) belongs to the consumer.
 *
 * A per-component EventSource matches the established pattern
 * (``useTwinSSE`` / ``useDocumentEvents``): ChatPanel unmounts inactive
 * tabs, so a KiAssistantTab-local subscription auto-scopes to the visible
 * tab. The connection opens on mount (not on isPending) to avoid churn and
 * a subscribe race with the first step of a turn.
 */
import { useEffect, useRef } from 'react';

export interface AssistantStepEvent {
  thread_id: string;
  step: {
    tool: string;
    iteration: number;
    index: number;
  };
}

interface UseAssistantStepEventsOptions {
  enabled?: boolean;
  onStep: (evt: AssistantStepEvent) => void;
}

const isStepEvent = (payload: unknown): payload is AssistantStepEvent => {
  if (typeof payload !== 'object' || payload === null) return false;
  const evt = payload as { thread_id?: unknown; step?: unknown };
  if (typeof evt.thread_id !== 'string') return false;
  if (typeof evt.step !== 'object' || evt.step === null) return false;
  return typeof (evt.step as { tool?: unknown }).tool === 'string';
};

export function useAssistantStepEvents(options: UseAssistantStepEventsOptions) {
  const { enabled = true, onStep } = options;
  // Latest-callback ref (the useChatSSE pattern) so the effect deps stay
  // [enabled] and a re-render never reopens the connection.
  const onStepRef = useRef(onStep);
  onStepRef.current = onStep;

  useEffect(() => {
    if (!enabled) return;

    const API_URL = import.meta.env.VITE_API_URL || '/api';
    const eventSource = new EventSource(`${API_URL}/events`, {
      withCredentials: true,
    });

    const handler = (e: MessageEvent) => {
      let payload: unknown = null;
      try {
        payload = e.data ? JSON.parse(e.data as string) : null;
      } catch {
        return; // malformed frame — drop silently (telemetry, not data)
      }
      if (!isStepEvent(payload)) return;
      onStepRef.current(payload);
    };

    eventSource.addEventListener('chat:assistant.step', handler);
    return () => {
      eventSource.removeEventListener('chat:assistant.step', handler);
      eventSource.close();
    };
  }, [enabled]);
}
